import React, { useContext, useState } from 'react';
import { LocaleContext } from '@rsc/contexts/LocaleContext';
import styled, { useTheme } from 'styled-components';
import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';
import { useLocalizedNavigation } from '@rsc/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

import Button from '../button/Button';
import USAIcon from '../svgs/usa-icon.svg';
import CanadaIcon from '../svgs/canada-icon.svg';
import sharedStyles from '../styles';

const ModalContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.65);
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: ${({ active }) => (active ? 'block' : 'none')};
  z-index: 5;
`;

const CountryPickerContainer = styled.div`
  position: absolute;
  align-items: center;
  display: flex;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgb(255, 255, 255);
  border-radius: 5px;
  width: 100%;
  min-height: 400px;
  max-width: 375px;
  color: ${({ theme }) => theme.colors.defaultFont};
`;

const Wrapper = styled.div`
  min-height: 360px;
  overflow-y: auto;

  h2 {
    margin: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 0.75rem;
  cursor: pointer;
  background: none;
  border: 0;

  &:hover {
    color: ${({ theme }) => theme.button.primary.backgroundHover};
  }
`;

const Header = styled.div`
  display: flex;
  padding: 0 ${sharedStyles.spacing['spacing-md']};
  width: 80%;

  p {
    font-size: 2rem;
    flex: 1 1 0%;
    margin: 0;
    line-height: 1.15;
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-weight: bold;
  }

  button {
    padding: 0;
    padding-left: ${sharedStyles.spacing['spacing-md']};
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
  }
`;

const DeliveryText = styled.p`
  padding: ${sharedStyles.spacing['spacing-md']};
  border-bottom: 1px solid gray;
  margin: 0 0 ${sharedStyles.spacing['spacing-md']};
  font-size: ${sharedStyles.fontSize.base};
`;

const CountryOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sharedStyles.spacing['spacing-lg']};
  padding: 0 ${sharedStyles.spacing['spacing-md']};

  input[type='radio'] {
    display: inline-block;
    cursor: pointer;
    margin-right: ${sharedStyles.spacing['spacing-md']};
  }

  button {
    width: fit-content;
  }

  label {
    display: inline-flex;
    align-items: center;
    font-weight: 700;

    &:hover {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  svg {
    width: 30px;
    height: 30px;
    margin-right: 10px;
  }

  span {
    display: inline;
  }
`;

function CountryPicker({ active }) {
  const { tt } = useUiComponentsInternationalContext();
  const navigate = useLocalizedNavigation();

  const localeContext = useContext(LocaleContext);
  const [activeCurrency, setActiveCurrency] = useState(
    localeContext?.currency || 'USD'
  );

  const isBrowser = typeof window !== 'undefined';

  const theme = useTheme();
  const iconColor = theme.colors.brandPrimary;

  const handleChange = event => {
    const selectedCountry = event.target.id;
    setActiveCurrency(selectedCountry);
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isBrowser) {
      // set currency in context
      localeContext.setCountryCookie();
      localeContext.updateCurrency(activeCurrency);
      localeContext.setCountryPickerActive(false);

      if (
        activeCurrency === 'CAD' &&
        !window.location.pathname.startsWith('/en-ca')
      ) {
        navigate(`/en-ca${window.location.pathname}${window.location.search}`);
      }

      if (
        activeCurrency === 'USD' &&
        window.location.pathname.startsWith('/en-ca')
      ) {
        if (window.location.pathname.startsWith('/en-ca/')) {
          navigate(
            `${window.location.pathname.replace('/en-ca/', '/')}${
              window.location.search
            }`
          );
        } else {
          navigate(
            `${window.location.pathname.replace('/en-ca', '/')}${
              window.location.search
            }`
          );
        }
      }
    }
  };

  return (
    <ModalContainer active={active}>
      <FocusTrap
        active={active}
        focusTrapOptions={{
          clickOutsideDeactivates: true,
        }}
      >
        <CountryPickerContainer active="true">
          <Wrapper>
            <CloseButton
              onClick={event => {
                event.preventDefault();
                localeContext.setCountryPickerActive(false);
              }}
            >
              <MdClose size={36} fill={iconColor} />
            </CloseButton>
            <Header>
              <p>
                {tt(s => s.notificationBannerCommon.chooseYourLocationHeader)}
              </p>
            </Header>
            <DeliveryText>
              {tt(
                s =>
                  s.notificationBannerCommon
                    .chooseYourLocationForCorrectInfoMessage
              )}
            </DeliveryText>
            <CountryOptions>
              <label htmlFor="CAD">
                <input
                  onChange={handleChange}
                  type="radio"
                  id="CAD"
                  name="currency"
                  value="CAD"
                  checked={activeCurrency === 'CAD'}
                />
                <CanadaIcon width={20} height={20} />
                <span>
                  {tt(s => s.notificationBannerCommon.canadaCadLabel)}
                </span>
              </label>
              <label htmlFor="USD">
                <input
                  onChange={handleChange}
                  type="radio"
                  id="USD"
                  name="currency"
                  value="USD"
                  checked={activeCurrency === 'USD'}
                />
                <USAIcon width={20} height={20} />
                <span>
                  {tt(s => s.notificationBannerCommon.unitedStatesUsdLabel)}
                </span>
              </label>
              <Button type="button" onClick={handleSubmit}>
                {tt(s => s.notificationBannerCommon.setLocationButton)}
              </Button>
            </CountryOptions>
          </Wrapper>
        </CountryPickerContainer>
      </FocusTrap>
    </ModalContainer>
  );
}

CountryPicker.propTypes = {
  active: PropTypes.bool.isRequired,
};

export default CountryPicker;
